import { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { MobileDrawer } from '../../mobile/MobileDrawer';
import { Sidebar } from '../Sidebar';
import { EmailStatusToast } from '@/components/domain/shared/EmailStatusToast';
import { BottomToastSlot } from '../BottomToastSlot';
import { useBottomToastStore } from '@/stores/bottomToastStore';
import {
  useAuth,
  useUnreadCount,
  useInvitations,
  usePermissions,
  useGmailConnectionStatus,
  useGmailAutoHeal,
  useTodayCount,
  type GmailConnectionStatus,
} from '@/hooks';
import { emailService, householdService } from '@/services';
import { useUserStore } from '@/stores/userStore';
import { useHouseholdStore } from '@/stores/householdStore';
import { SwitchHouseholdModal } from '@/components/domain/shared/SwitchHouseholdModal';
import type { MyHousehold } from '@/services/household.service';
import { SystemBannerSlot } from '@/components/domain/shared/SystemBannerSlot';
import { FeedbackModalHost } from '@/components/domain/shared/FeedbackModal';
import { useFeedbackStore } from '@/stores/feedbackStore';
import { DEFAULT_HOUSEHOLD_EMOJI } from '@/constants/household';
import { getAccessLevelLabel } from '@/constants/accessLevels';

type TabKey = 'today' | 'review' | 'schedule' | 'settings';

type ConnectionStatusUi =
  | 'connected'
  | 'not_connected'
  | 'connection_issue'
  | 'paused';

function mapGmailStatusToConnectionUi(
  status: GmailConnectionStatus | undefined,
): ConnectionStatusUi {
  if (!status) return 'not_connected';
  if (status.state === 'connected') return 'connected';
  if (status.state === 'paused') return 'paused';
  if (
    status.state === 'expired' ||
    status.state === 'expiring_soon' ||
    status.state === 'needs_attention'
  ) {
    return 'connection_issue';
  }
  return 'not_connected';
}

function getActiveTab(pathname: string): TabKey {
  if (pathname.startsWith('/review')) return 'review';
  if (pathname.startsWith('/schedule')) return 'schedule';
  if (pathname.startsWith('/settings')) return 'settings';
  return 'today';
}

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [reviewCount, setReviewCount] = useState(0);
  // Bumped each time an email changes (the status toast's realtime feed) or a
  // page reports a mutation. Forwarded
  // to MobileDrawer so the emails-processed card re-fetches its count /
  // quota without waiting for a full page reload.
  const [refreshKey, setRefreshKey] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const activeTab = getActiveTab(location.pathname);
  const unreadCount = useUnreadCount();
  const { invitationNotifications } = useInvitations();
  const { canReview, canEdit, accessLevel, isOwner } = usePermissions();

  // Total unread includes pending invitations
  const totalUnread = unreadCount + invitationNotifications.length;
  const [showDrawer, setShowDrawer] = useState(false);
  // The page element that opened the drawer (the menu button). The drawer's
  // feedback card hands focus back here when the modal closes — the card
  // itself is inside the closed drawer (YAR-484 AC 9).
  const drawerOpenerRef = useRef<HTMLElement | null>(null);
  const openFeedback = useFeedbackStore((s) => s.openFeedback);
  // The active household and the level in it now live in one store, seeded
  // synchronously from the pin (so the first paint already has an id to
  // query with) and published authoritatively by AuthContext during boot.
  // The shell no longer resolves them itself: it used to redo the whole
  // auth.getUser -> users -> household_members waterfall that boot had just
  // finished, purely to learn the same id.
  const householdId = useHouseholdStore((s) => s.householdId);
  const householdResolved = useHouseholdStore((s) => s.resolved);
  const userTimezone = useUserStore((s) => s.timezone);
  const profileDisplayName = useUserStore((s) => s.displayName);
  // Today's item count for the nav badge (YAR-399). Shares `refreshKey` with
  // the household card so completing a task or approving an email re-counts
  // without a reload — the badge has to decrement live, not at next boot.
  const todayCount = useTodayCount(householdId, userTimezone, refreshKey);
  const [householdName, setHouseholdName] = useState('');
  const [householdEmoji, setHouseholdEmoji] = useState<string>(
    DEFAULT_HOUSEHOLD_EMOJI,
  );
  const [householdEmojiColor, setHouseholdEmojiColor] = useState<
    string | undefined
  >(undefined);
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [myHouseholds, setMyHouseholds] = useState<MyHousehold[]>([]);
  const [householdImagePath, setHouseholdImagePath] = useState<string | null>(
    null,
  );
  const { data: gmailStatus } = useGmailConnectionStatus(householdId);
  // Proactively re-arm a watch that's expiring/expired using the stored
  // refresh_token, so the user isn't nagged to "reconnect" for a connection
  // that heals itself server-side. No-op for healthy/revoked/not-connected.
  useGmailAutoHeal(householdId);
  const connectionStatus = mapGmailStatusToConnectionUi(gmailStatus);

  const userAccessLevel = getAccessLevelLabel(accessLevel);

  // Re-resolve the active household AND the level in it, then publish both
  // to the store. Used after anything that can change membership (accepting
  // an invitation, leaving, a switch) — NOT on every mount: AuthContext
  // already resolved this during boot, and repeating it here was one of the
  // duplicate waterfalls this refactor removes.
  //
  // Household and role are always written together. The level is
  // per-household — owner of yours, viewer in another — so publishing one
  // without the other is how a viewer ends up holding owner-level UI.
  const refreshHousehold = useCallback(async () => {
    try {
      const membership =
        await householdService.resolveActiveMembershipForCurrentUser();
      if (!membership) {
        // No active membership anywhere: they left, were removed, or the
        // household was deleted. Drop the pin so the next load doesn't try
        // to paint a household they can no longer see.
        householdService.clearStaleActiveHousehold();
        useHouseholdStore.getState().setActiveHousehold(null, null);
        useUserStore.getState().setRole(null);
        useUserStore.getState().updateProfile({ profileRole: null });
        return;
      }
      useHouseholdStore
        .getState()
        .setActiveHousehold(membership.householdId, membership.role);
      useUserStore.getState().setRole(membership.role);
      // Per-household family role (YAR-485), from the same row.
      useUserStore
        .getState()
        .updateProfile({ profileRole: membership.familyRole ?? null });
    } catch {
      /* silent — the next auth refresh re-resolves */
    }
  }, []);

  // Display fields follow whatever household is active, including the
  // optimistic pin. Keyed off householdId alone, so it covers the first
  // paint and every later switch with one code path.
  useEffect(() => {
    if (!householdId) {
      setHouseholdName('');
      return;
    }
    let cancelled = false;
    void householdService
      .getHousehold(householdId)
      .then((household) => {
        if (cancelled || !household) return;
        setHouseholdName(household.name ?? '');
        setHouseholdEmoji(household.emoji_avatar ?? DEFAULT_HOUSEHOLD_EMOJI);
        setHouseholdEmojiColor(household.emoji_color ?? undefined);
        setHouseholdImagePath(household.avatar_image_path ?? null);
      })
      .catch(() => {
        /* chrome falls back to defaults; data pages surface their own errors */
      });
    return () => {
      cancelled = true;
    };
  }, [householdId]);

  useEffect(() => {
    // Boot normally publishes this before the shell ever mounts. Only
    // resolve here if it somehow didn't, so the shell still works if it is
    // mounted outside the usual auth flow.
    if (!useHouseholdStore.getState().resolved) void refreshHousehold();
  }, [refreshHousehold]);

  // ── Household switcher ────────────────────────────────────────────────
  const openHouseholdSwitcher = useCallback(async () => {
    setShowSwitcher(true);
    try {
      setMyHouseholds(await householdService.getMyHouseholdsForCurrentUser());
    } catch {
      /* the modal renders an empty list rather than blocking the UI */
    }
  }, []);

  // Pin the chosen household, then re-resolve BOTH the household and the
  // role. The role is per-household, so carrying the previous one over
  // would leave a viewer holding owner-level UI until the next reload.
  const handleSelectHousehold = useCallback(
    async (nextId: string) => {
      setShowSwitcher(false);
      if (nextId === householdId) return;
      // An undo toast belongs to the household it was raised in.
      useBottomToastStore.getState().dismissAction(undefined, 'route');
      householdService.setActiveHouseholdPin(nextId);
      // Point at the new household with the level UNKNOWN. This is the
      // important half: it drops the previous household's role instead of
      // letting it ride until the re-resolve lands, which would leave an
      // owner-of-one-household holding owner UI inside a household they
      // only view.
      useHouseholdStore.getState().beginSwitch(nextId);
      useUserStore.getState().setRole(null);
      useUserStore.getState().updateProfile({ profileRole: null });
      // Clear household-scoped shell state so the old household's numbers
      // don't linger in the chrome while the new ones load.
      setReviewCount(0);
      await refreshHousehold();
      navigate('/today');
    },
    [householdId, refreshHousehold, navigate],
  );

  // YAR-503: an account owns at most ONE household but may join any number,
  // so someone who only joined (Co-Admin, Editor, Viewer) can still start
  // their own. Ownership comes from the membership list, never the active
  // role — an owner viewing someone else's household reads as Editor there.
  const ownsNoHousehold =
    myHouseholds.length > 0 && !myHouseholds.some((h) => h.role === 'PRIMARY');
  const handleCreateNewHousehold = useCallback(() => {
    setShowSwitcher(false);
    navigate('/create-household');
  }, [navigate]);

  // Fetch review count periodically AND whenever an email changes
  const fetchCount = useCallback(async () => {
    if (!householdId) return;
    try {
      const count = await emailService.getReviewCount(householdId);
      setReviewCount(count);
    } catch {
      /* silent */
    }
  }, [householdId]);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  // Pages call this after a mutation the shell can't see (a task completed
  // on Today, an item deleted). The email status feed calls it too, on
  // every change to an email (approve, dismiss, a read finishing), which is
  // what keeps the Review count, plan meter and Today badge live.
  const refreshChrome = useCallback(() => {
    fetchCount();
    setRefreshKey((k) => k + 1);
  }, [fetchCount]);

  // Responsive breakpoint listener
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const openDrawer = useCallback(() => {
    const focused = document.activeElement;
    drawerOpenerRef.current =
      focused instanceof HTMLElement && focused !== document.body
        ? focused
        : null;
    setShowDrawer(true);
  }, []);

  // YAR-482: close the drawer, then open the one Send feedback modal over
  // the current page — no navigation. Blur first so focus never stays on
  // the card inside the closed drawer, even when no opener was recorded
  // (a tap on iOS doesn't focus the menu button).
  const handleDrawerFeedback = useCallback(() => {
    setShowDrawer(false);
    const focused = document.activeElement;
    if (focused instanceof HTMLElement) focused.blur();
    openFeedback({ returnFocusTo: drawerOpenerRef.current });
  }, [openFeedback]);

  const handleSidebarNavigate = (path: string) => {
    navigate(path);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('[AppShell] Sign out error:', error);
    }
  };

  // The profile name the user chose at onboarding, not a guess at it. The
  // email local-part fallback stays for a profile that has none, but it is a
  // fallback: `anamsdawson@` used to render as "Anamsdawson" even though
  // `users.display_name` held "Ana Dawson" all along.
  const getDisplayName = () => {
    const chosen = profileDisplayName?.trim();
    if (chosen) return chosen;
    if (!user?.email) return 'User';
    const name = user.email.split('@')[0];
    return name
      .split('.')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  if (isMobile) {
    return (
      <div className="ph-no-capture flex flex-col min-h-dvh bg-background">
        {/*
          Banner sits above the page, full-width. The mobile drawer
          opens on top of it (overlay), so we don't need to suppress it
          when the drawer is visible.
        */}
        <SystemBannerSlot
          scope="global"
          householdId={householdId}
        />
        {/*
          Carries `bg-surface` so an under-filled page (empty Today/Review,
          etc.) doesn't expose the root's `bg-background` (white) below its
          content. Page roots set `bg-surface` themselves, but `min-h-full`
          on a flex-1 parent doesn't reliably extend in every browser, so we
          layer the same color one level up.

          Deliberately NOT `overflow-y-auto`. This element is `flex-1` of a
          `min-h-dvh` column, so it has no upper bound: its height is its
          content's height and it never actually scrolled — html/body is the
          page scroller in production (see index.css). But `overflow-y: auto`
          still made it a scroll CONTAINER, and `position: sticky` pins to
          the nearest scroll container. So every `sticky top-0` header inside
          it pinned to a scrollport that never moves, and rode the document
          scroll straight off the top of the screen. That is YAR-389: the
          Settings sub-page header is authored sticky and was not.
        */}
        <main className="flex-1 bg-surface flex flex-col">
          <Outlet
            // Remount every household-scoped page when the active household
            // changes. Switching only swaps `householdId`; without this key
            // the mounted pages keep the state they already fetched and the
            // user sees the PREVIOUS household's data until something happens
            // to refetch — the exact "session persisted after switching" bug
            // reported against the old switcher.
            key={householdId ?? 'no-household'}
            context={{
              onOpenDrawer: openDrawer,
              userName: getDisplayName(),
              householdId,
              householdName,
              householdEmoji,
              householdEmojiColor,
              householdImagePath,
              userAccessLevel,
              householdResolved,
              refreshHousehold,
              refreshChrome,
              refreshKey,
              reviewCount,
              openHouseholdSwitcher,
              switchHousehold: handleSelectHousehold,
            }}
          />
        </main>
        <MobileDrawer
          isOpen={showDrawer}
          onClose={() => setShowDrawer(false)}
          activeItem={`/${activeTab}`}
          reviewCount={reviewCount}
          todayCount={todayCount}
          unreadNotifications={totalUnread}
          onNavigate={handleSidebarNavigate}
          onNotificationsClick={() => {
            setShowDrawer(false);
            navigate('/activity');
          }}
          householdId={householdId}
          refreshKey={refreshKey}
          householdName={householdName}
          householdEmoji={householdEmoji}
          householdEmojiColor={householdEmojiColor}
          householdImagePath={householdImagePath}
          userAccessLevel={userAccessLevel}
          connectionStatus={connectionStatus}
          showReviewTab={canReview}
          showUpgradeCta={isOwner}
          // YAR-489: plan usage is for Admin and Co-Admin (managers), not
          // Editors or Viewers; only the Admin can open the Plan page.
          showUsageCard={canReview}
          onFeedbackClick={handleDrawerFeedback}
          onCreateClick={
            canEdit
              ? () => {
                  setShowDrawer(false);
                  navigate('/schedule?create=true');
                }
              : undefined
          }
        />
        <SwitchHouseholdModal
          open={showSwitcher}
          onClose={() => setShowSwitcher(false)}
          households={myHouseholds.map((h) => ({
            id: h.id,
            name: h.name,
            emoji: h.emoji ?? DEFAULT_HOUSEHOLD_EMOJI,
            emojiColor: h.emojiColor ?? undefined,
            imagePath: h.imagePath,
            accessLevel: getAccessLevelLabel(h.role),
            isCurrent: h.id === householdId,
          }))}
          onSelect={handleSelectHousehold}
          onCreateNew={ownsNoHousehold ? handleCreateNewHousehold : undefined}
        />
        <FeedbackModalHost />
        <EmailStatusToast
          householdId={householdId}
          onEmailsChanged={refreshChrome}
          refreshKey={refreshKey}
        />
        {/* The one bottom toast slot (YAR-476): undo + email status lanes. */}
        <BottomToastSlot />
      </div>
    );
  }

  // Desktop layout with Sidebar
  return (
    <div className="ph-no-capture flex flex-col min-h-dvh bg-background">
      {/*
        Banner is the first child of the shell so it spans full width
        across sidebar and main, and pushes both columns down. Sidebar
        and main share the remaining row height below it.
      */}
      <SystemBannerSlot
        scope="global"
        householdId={householdId}
      />
      <div className="flex flex-1 min-h-0">
        <Sidebar
          activeItem={`/${activeTab}`}
          userName={getDisplayName()}
          userEmail={user?.email ?? ''}
          reviewCount={reviewCount}
          todayCount={todayCount}
          onNavigate={handleSidebarNavigate}
          onSignOut={handleSignOut}
          showReviewTab={canReview}
        />
        {/* Same rationale as mobile, both halves of it: keep the column
            coloured with the page surface so a short page doesn't reveal
            the root's white `bg-background`, and no `overflow-y-auto` —
            it is unbounded here too, so it only ever broke `sticky`. */}
        <div className="flex-1 min-w-0 bg-surface">
          <main className="p-6">
            <Outlet
              // See the mobile branch above: keying on the active household
              // guarantees no page carries state across a switch.
              key={householdId ?? 'no-household'}
              context={{
                userName: getDisplayName(),
                householdId,
                householdName,
                householdEmoji,
                householdEmojiColor,
                householdImagePath,
                userAccessLevel,
                householdResolved,
                refreshHousehold,
                refreshChrome,
                refreshKey,
                reviewCount,
                openHouseholdSwitcher,
                switchHousehold: handleSelectHousehold,
              }}
            />
          </main>
        </div>
      </div>
      <SwitchHouseholdModal
        open={showSwitcher}
        onClose={() => setShowSwitcher(false)}
        households={myHouseholds.map((h) => ({
          id: h.id,
          name: h.name,
          emoji: h.emoji ?? DEFAULT_HOUSEHOLD_EMOJI,
          emojiColor: h.emojiColor ?? undefined,
          imagePath: h.imagePath,
          accessLevel: getAccessLevelLabel(h.role),
          isCurrent: h.id === householdId,
        }))}
        onSelect={handleSelectHousehold}
        onCreateNew={ownsNoHousehold ? handleCreateNewHousehold : undefined}
      />
      <FeedbackModalHost />
      <EmailStatusToast
        householdId={householdId}
        onEmailsChanged={refreshChrome}
        refreshKey={refreshKey}
      />
      {/* Mobile-first, but the slot also renders on desktop (384px max) so
          a completion there is still undoable. */}
      <BottomToastSlot />
    </div>
  );
}
